import React, { useState } from 'react'
import { ArrowRight, Loader2 } from 'lucide-react'
import { GamerTagService } from '@/features/enrollment'
import ValidationErrorDisplay from '../../shared/ValidationErrorDisplay'
import ReportProblemButton from './ReportProblemButton'
import { Step1GamingPlatformProps, GamerTagResult } from '@/features/enrollment'
import PlatformSelection from './PlatformSelection'
import PendingApplications from './PendingApplications'

export default function Step1GamingPlatform({
  enrollmentData,
  updateEnrollmentData,
  updatePlatformData,
  pendingApplications,
  showPendingApplications,
  setShowPendingApplications,
  handleNavigate,
  onRefreshPendingApplications,
  isNavigating,
  isCompleting,
}: Step1GamingPlatformProps) {
  const [gamerTagResults, setGamerTagResults] = useState<Record<string, GamerTagResult | null>>({})
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({})
  const [reportedProblems, setReportedProblems] = useState<Record<string, boolean>>({})

  const handleCheckGamerTag = async (platform: string) => {
    const gamerTag = enrollmentData.platforms?.[platform]?.gamerTag
    if (!gamerTag) {
      alert('Please enter a gamer tag first')
      return
    }
    setLoadingStates(prev => ({ ...prev, [platform]: true }))

    try {
      const result = await GamerTagService.checkGamerTag(gamerTag, platform)
      setGamerTagResults(prev => ({ ...prev, [platform]: result }))
      
      // Only store successful verifications to database
      if (result.pass) {
        // Automatically set gamer tag as captured when verification is successful
        updatePlatformData(platform, 'gamerTagCaptured', true)
        // Store the successful verification data
        updatePlatformData(platform, 'verificationResult', {
          gamer_tag: result.gamer_tag,
          totalHoursPlayed: result.totalHoursPlayed,
          pass: result.pass,
          verifiedAt: new Date().toISOString()
        })
        // Keep the gamer tag in the database for successful verifications
      } else {
        // For failed verifications, clear the gamer tag from database and reset captured status
        updatePlatformData(platform, 'gamerTag', '')
        updatePlatformData(platform, 'gamerTagCaptured', false)
        updatePlatformData(platform, 'verificationResult', null)
      }
    } catch (error: any) {
      console.error('Error checking gamer tag:', error)
      
      // Check if it's an API error response (has data) vs network error
      if (error.response && error.response.data) {
        // API returned an error response, show it on platform
        const apiError = error.response.data
        const errorResult = {
          gamer_tag: gamerTag,
          totalHoursPlayed: 0,
          pass: false,
          error: apiError.error || 'API Error',
          details: apiError.details || 'Gamer Tag not found or is private'
        }
        setGamerTagResults(prev => ({ ...prev, [platform]: errorResult }))
        // Clear failed gamer tag from database
        updatePlatformData(platform, 'gamerTag', '')
        updatePlatformData(platform, 'gamerTagCaptured', false)
        updatePlatformData(platform, 'verificationResult', null)
      } else {
        // Network/connection error - show on platform
        const errorResult = {
          gamer_tag: gamerTag,
          totalHoursPlayed: 0,
          pass: false,
          error: 'Network Error',
          details: 'Failed to connect to verification service. Please try again.'
        }
        setGamerTagResults(prev => ({ ...prev, [platform]: errorResult }))
        // Clear failed gamer tag from database
        updatePlatformData(platform, 'gamerTag', '')
        updatePlatformData(platform, 'gamerTagCaptured', false)
        updatePlatformData(platform, 'verificationResult', null)
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, [platform]: false }))
    }
  }

  const handleReportProblem = async (platform: string) => {
    try {
      
      // Mark as reported
      setReportedProblems(prev => ({ ...prev, [platform]: true }))
      
      // Optionally, you could also mark the platform as "skipped due to technical issues"
      // updateEnrollmentData('gamerTagsSkipped', true)
      
      alert(`Problem reported for ${platform} gamer tag verification. You can now skip gamer tag verification to continue.`)
    } catch (error) {
      console.error('Error reporting problem:', error)
      alert('Failed to report problem. Please try again.')
    }
  }

  // Check if step can proceed (either all platforms verified or gamer tags skipped)
  const canProceed = () => {
    const selectedPlatforms = enrollmentData.selectedPlatforms || []
    
    // If no platforms selected, only proceed if skipped
    if (selectedPlatforms.length === 0) {
      return enrollmentData.gamerTagsSkipped
    }
    
    // Check if all selected platforms are verified
    const allVerified = selectedPlatforms.every((platform: string) => {
      const platformData = enrollmentData.platforms?.[platform]
      return platformData && platformData.gamerTagCaptured && platformData.verificationResult && platformData.verificationResult.pass
    })
    
    // If all platforms are verified, automatically clear the skip status
    if (allVerified && enrollmentData.gamerTagsSkipped) {
      // Use setTimeout to avoid state update during render
      setTimeout(() => {
        updateEnrollmentData('gamerTagsSkipped', false)
      }, 0)
    }
    
    // Proceed if either skipped or all verified
    return enrollmentData.gamerTagsSkipped || allVerified
  }

  // Get list of unverified platforms
  const getUnverifiedPlatforms = () => {
    const selectedPlatforms = enrollmentData.selectedPlatforms || []
    return selectedPlatforms.filter((platform: string) => {
      const platformData = enrollmentData.platforms?.[platform]
      return !platformData || !platformData.gamerTagCaptured || !platformData.verificationResult || !platformData.verificationResult.pass
    })
  }

  // Get validation errors for Step 1
  const getValidationErrors = (): string[] => {
    const errors: string[] = []
    const selectedPlatforms = enrollmentData.selectedPlatforms || []
    
    if (selectedPlatforms.length === 0 && !enrollmentData.gamerTagsSkipped) {
      errors.push('Please select at least one gaming platform or skip gamer tag verification')
    }
    
    if (selectedPlatforms.length > 0 && !canProceed()) {
      const unverified = getUnverifiedPlatforms()
      if (unverified.length > 0 && !enrollmentData.gamerTagsSkipped) {
        errors.push(`Gamer tags must be verified for: ${unverified.map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')}`)
        errors.push('Alternatively, you can skip gamer tag verification if the verification tool is having issues')
      }
    }
    
    return errors
  }


  return (
    <div className="space-y-6">
      <PlatformSelection
        enrollmentData={enrollmentData}
        updateEnrollmentData={updateEnrollmentData}
        updatePlatformData={updatePlatformData}
        onCheckGamerTag={handleCheckGamerTag}
        loadingStates={loadingStates}
        gamerTagResults={gamerTagResults}
        canProceed={canProceed}
        getUnverifiedPlatforms={getUnverifiedPlatforms}
        onReportProblem={handleReportProblem}
        reportedProblems={reportedProblems}
      />

      <div className="flex justify-end">
        <button
          className={`flex items-center space-x-2 ${
            canProceed()
              ? 'btn-primary' 
              : 'btn-secondary opacity-50 cursor-not-allowed'
          }`}
          onClick={() => {
            // If gamer tags are skipped, allow continuation
            if (enrollmentData.gamerTagsSkipped) {
              handleNavigate(1)
              return
            }
            
            // Otherwise, require platform selection and verification
            if (!enrollmentData.selectedPlatforms || enrollmentData.selectedPlatforms.length === 0) {
              alert('Please select at least one gaming platform to continue.')
              return
            }
            
            if (!canProceed()) {
              const unverified = getUnverifiedPlatforms()
              alert(`Please verify gamer tags for all selected platforms before continuing.\n\nUnverified platforms: ${unverified.map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')}`)
              return
            }
            
            // Clean up failed verifications before proceeding
            const cleanedPlatforms = { ...enrollmentData.platforms }
            
            // Remove failed verification data from platforms
            Object.keys(cleanedPlatforms).forEach(platform => {
              const platformData = cleanedPlatforms[platform as keyof typeof cleanedPlatforms]
              if (platformData) {
                // If gamer tag verification failed, clear the gamer tag and reset captured status
                const result = gamerTagResults[platform]
                if (result && ('error' in result || !result.pass)) {
                  // Clear failed verification data
                  updatePlatformData(platform, 'gamerTag', '')
                  updatePlatformData(platform, 'gamerTagCaptured', false)
                  updatePlatformData(platform, 'verificationResult', null)
                }
              }
            })
            
            handleNavigate(1)
          }}
          disabled={!canProceed() || isNavigating}
        >
          {isNavigating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ArrowRight className="w-4 h-4" />
          )}
          <span>{isNavigating ? 'Saving...' : 'Continue'}</span>
        </button>
      </div>

      <ValidationErrorDisplay 
        errors={getValidationErrors()} 
        title="Cannot continue to Step 2 - Please resolve:"
      />

      <PendingApplications
        pendingApplications={pendingApplications}
        showPendingApplications={showPendingApplications}
        setShowPendingApplications={setShowPendingApplications}
        onRefreshPendingApplications={onRefreshPendingApplications}
      />
    </div>
  )
}

